/**
 * WebSocket event contracts (Socket.IO).
 *
 * Separation rationale: Stream / Outbox events (events/) live on the server
 * event bus. WS events are the *client-facing* shape pushed through the
 * Socket.IO gateway to the React console. Keeping them separate means
 * internal event refactors don't accidentally break the frontend contract.
 *
 * Convention:
 *   - server-to-client events use past-tense ('lead.created')
 *   - client-to-server events use imperative ('conversation.subscribe')
 *   - all payloads are zod-validated on both ends
 */
import { z } from 'zod';
import { LeadSummary } from '../dto/lead.js';
import { ConversationMessage } from '../dto/conversation.js';
import { Notification } from '../dto/notification.js';
// ---------- server → client ----------
export const WsLeadCreated = z.object({
    type: z.literal('lead.created'),
    lead: LeadSummary,
});
export const WsLeadAssigned = z.object({
    type: z.literal('lead.assigned'),
    leadId: z.string().uuid(),
    assignedSalesId: z.string().uuid(),
    version: z.number().int(),
});
export const WsLeadStateChanged = z.object({
    type: z.literal('lead.state_changed'),
    lead: LeadSummary,
});
export const WsMessageIn = z.object({
    type: z.literal('message.in'),
    conversationId: z.string().uuid(),
    message: ConversationMessage,
});
export const WsMessageOut = z.object({
    type: z.literal('message.out'),
    conversationId: z.string().uuid(),
    message: ConversationMessage,
});
export const WsNotification = z.object({
    type: z.literal('notification'),
    notification: Notification,
});
/** Union of all server→client events. Client switch on `type`. */
export const WsServerEvent = z.discriminatedUnion('type', [
    WsLeadCreated,
    WsLeadAssigned,
    WsLeadStateChanged,
    WsMessageIn,
    WsMessageOut,
    WsNotification,
]);
// ---------- client → server ----------
export const WsSalesOnline = z.object({
    type: z.literal('sales.online'),
});
export const WsConversationSubscribe = z.object({
    type: z.literal('conversation.subscribe'),
    conversationId: z.string().uuid(),
});
export const WsConversationUnsubscribe = z.object({
    type: z.literal('conversation.unsubscribe'),
    conversationId: z.string().uuid(),
});
export const WsClientEvent = z.discriminatedUnion('type', [
    WsSalesOnline,
    WsConversationSubscribe,
    WsConversationUnsubscribe,
]);
// ---------- room naming convention ----------
export const WsRooms = {
    /** All sales users in a tenant — broadcast lead.created etc. */
    tenantSales: (tenantId) => `tenant:${tenantId}:sales`,
    /** A single sales user — direct notifications. */
    salesUser: (salesId) => `sales:${salesId}`,
    /** Per-conversation room for IM streaming. */
    conversation: (conversationId) => `conversation:${conversationId}`,
};
//# sourceMappingURL=index.js.map